import { Box, Grid, Typography } from '@material-ui/core';
import Dashboard from '@material-ui/icons/Dashboard';
import ViewModule from '@material-ui/icons/ViewModule';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { CategorySearchType } from 'src/common/enumerations/category-search-type';
import XLRLogger from 'src/common/helpers/logger';
import { AppState } from 'src/common/redux/configure-store';
import SearchProducts from 'src/search-products/search-products';
import SearchTransactions from 'src/search-transactions/search-transactions';
import VerticalCenter from 'src/vertical-center/vertical-center';
import DisplayModeButton from './components/display-mode-button';
import SearchBar from './components/search-bar/search-bar';
import DoubleTrainingModeWatermark from 'src/common/training-mode/double-training-mode-watermark';
import { styles } from './search-styles';
import EmeraldModal from '../common/components/emerald-modal/emerald-modal';
import { useTranslation } from 'react-i18next';

const loggerSource = 'search.tsx';
const SEARCH_MODAL = 'SEARCH_MODAL';
const defaultItemsPerPage = 4;

export const showSearchModal = (props: IsearchProps): void => {
  pushEmeraldModal(<SerachModal {...props} />, undefined, SEARCH_MODAL);
};

export const closeSearchModal = (): void => popEmeraldModal(SEARCH_MODAL);

type ISearchProps = ISearchState & IsearchProps;

export const Search: React.FC<ISearchProps> = (props: ISearchProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { searchType, onClose, onSumbit, isReturnMode } = props;

  const resultSummary = useCallback(() => {
    if (isLoading) {
      return '';
    }
    if (searchText && !searchText.trim()) {
      return '';
    }

    // handle the click event
    switch (searchType) {
      case CategorySearchType.Products:
        return `${totalCount} ${
          totalCount === 1 ? t('SearchItem_ResultsCount') : t('SearchItem_ResultsCount_Plural')
        }`;
      case CategorySearchType.Transactions:
        return `${totalCount} ${
          totalCount === 1
            ? t('SearchTransaction_ResultsCount')
            : t('SearchTransaction_ResultsCount_Plural')
        }`;
      default:
        return t('SearchPanel_Search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, searchType, totalCount, isLoading]);

  useEffect((): void => {
    setTotalCount(totalCount);
    XLRLogger.debug({
      message: `Search Category: ${searchType}; Summary: ${resultSummary()}`,
      source: loggerSource,
    });
  }, [resultSummary, searchType, totalCount]);

  return (
    <EmeraldModal
      open
      classes={{
        paper: props.trainingModeActive ? classes.trainingModeBackground : classes.dialogPaper,
      }}
    >
      <Grid
        container
        className={`fullHeight ${classes.main} ${classes.modal} ${
          props.trainingModeActive ? classes.trainingModeBackground : classes.background
        }`}
      >
        {props.trainingModeActive && <DoubleTrainingModeWatermark />}
        <Grid container className={classes.searchContainer}>
          <Grid item xs={12} className='fullWidth' style={{ height: 'fit-content' }}>
            <Grid
              container
              direction='row'
              spacing={0}
              className={`${
                searchType === CategorySearchType.Products ? classes.searchContainerProducts : ''
              }`}
            >
              <Grid item xs={searchType === CategorySearchType.Transactions ? 3 : 2}>
                <Grid
                  container
                  direction='column'
                  className={`${classes.searchTitleContainer} fullWidth`}
                >
                  <Typography
                    className={`${classes.searchTitleHeader} ${classes.searchTitleHeaderLabel}`}
                    data-testid='search-title-text'
                  >
                    {isReturnMode
                      ? t('HeaderControl_ReturnItem')
                      : t(getSearchWindowTitle(searchType))}
                  </Typography>
                  <Grid item xs={3}>
                    <Typography className={`${classes.searchTitleBar}`} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={searchType === CategorySearchType.Transactions ? 8 : 9}>
                <SearchBar
                  inputText={searchText}
                  placeholder={t(getSearchBarPlaceholder(searchType))}
                  onChange={setSearchText}
                  disabled={searchText.trim() === ''}
                  hideCloseIcon={searchType === CategorySearchType.Products}
                />
              </Grid>
              <Grid item xs={1}>
                <BaseButton
                  onClick={onClose}
                  buttonSize={0}
                  className={classes.closeButton}
                  data-testid={`search-button-dialog-close`}
                >
                  {t('Cancel')}
                </BaseButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            xs={12}
            justifyContent='space-between'
            style={{ height: 'fit-content' }}
          >
            {!(searchType === CategorySearchType.Products && !searchText) && (
              <Grid item>
                <VerticalCenter>
                  <Box
                    mb={2}
                    mt={1}
                    className={`${classes.resultSummary} ${
                      searchType === CategorySearchType.Transactions
                        ? classes.resultSummaryTransaction
                        : classes.resultSummaryProducts
                    }`}
                  >
                    <span data-testid={`search-total-summary`}>{resultSummary()}</span>
                  </Box>
                </VerticalCenter>
              </Grid>
            )}
            {searchType === CategorySearchType.Transactions && (
              <Grid item xs={2}>
                <Grid container justifyContent={'flex-end'} className={classes.searchTransactions}>
                  <DisplayModeButton
                    icon={<ViewModule className={classes.icon} />}
                    selected={false}
                    disabled
                  />
                  <DisplayModeButton icon={<Dashboard className={classes.icon} />} selected />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            className={`${
              searchType === CategorySearchType.Transactions
                ? classes.searchResults
                : classes.searchResultsProduct
            } ${props.trainingModeActive ? classes.trainingModeBackground : ''} flexFull`}
          >
            {searchType === CategorySearchType.Products && (
              <SearchProducts
                searchText={searchText}
                pageSize={defaultItemsPerPage}
                submitButton={{
                  label: t(isReturnMode ? 'SearchPanel_Return' : 'Add_Item'),
                  onClick: onSumbit,
                }}
                setTotalCount={setTotalCount}
                setIsLoading={setIsLoading}
              />
            )}
            {searchType === CategorySearchType.Transactions && (
              <SearchTransactions
                searchText={searchText}
                setTotalCount={setTotalCount}
                onSubmit={onSumbit}
                submitButtonText={props.submitButtonText}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </EmeraldModal>
  );
};

interface ISearchState {
  trainingModeActive: boolean;
  isReturnMode: boolean;
}

const mapStateToProps = (state: AppState): ISearchState => ({
  trainingModeActive: state.posMode.trainingMode,
  isReturnMode: !!state.posMode.returnMode.returnType,
});

const SerachModal = connect(mapStateToProps)(Search);
export default SerachModal;

export interface IsearchProps {
  searchType: CategorySearchType;
  onClose: () => void;
  onSumbit: Function;
  submitButtonText: string;
}

export const getSearchBarPlaceholder = (category: CategorySearchType): string => {
  switch (category) {
    case CategorySearchType.Products:
      return 'SearchItems_Command';
    case CategorySearchType.Transactions:
      return 'SearchTransactionPOSName';
    default:
      return 'Search';
  }
};
export const getSearchWindowTitle = (category: CategorySearchType): string => {
  switch (category) {
    case CategorySearchType.Products:
      return 'SearchItem_Command';
    case CategorySearchType.Transactions:
      return 'TransactionReturn_Search_Transaction';
    default:
      return 'Search';
  }
};
